import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ChatWidgetService } from './chatWidgetService';
import type { ChatWidgetConfig, ChatMessage as Message, ChatStorageData } from './types';

interface ChatWidgetProps {
  config: ChatWidgetConfig;
}

const STORAGE_KEY = 'pospeh-chat-widget';
const DEFAULT_EXPIRATION_MINUTES = 60;

// Storage utilities
const getStorageData = (): ChatStorageData | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    const parsed: ChatStorageData = JSON.parse(data);

    // Check if session has expired
    const lastMessageAt = new Date(parsed.lastMessageAt);
    const expirationMs = parsed.expirationMinutes * 60 * 1000;
    const now = new Date();

    if (now.getTime() - lastMessageAt.getTime() > expirationMs) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Convert timestamps back to Date objects
    parsed.messages = parsed.messages.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp)
    }));

    return parsed;
  } catch {
    return null;
  }
};

const saveStorageData = (data: ChatStorageData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

const clearStorageData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

// Markdown renderer component for reuse
const MarkdownContent: React.FC<{ content: string; isUser?: boolean }> = ({ content, isUser }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeRaw]}
    components={{
      // Custom blockquote for callouts
      blockquote: ({ children, ...props }) => {
        const contentStr = String(children);
        // Check for callout patterns
        if (contentStr.includes('⚠️ Warning:') || contentStr.includes('Warning:')) {
          return (
            <div className="pospeh-callout pospeh-callout-warning">
              {children}
            </div>
          );
        }
        if (contentStr.includes('ℹ️ Important') || contentStr.includes('Important:')) {
          return (
            <div className="pospeh-callout pospeh-callout-info">
              {children}
            </div>
          );
        }
        if (contentStr.includes('💡 Tip') || contentStr.includes('Tip:')) {
          return (
            <div className="pospeh-callout pospeh-callout-tip">
              {children}
            </div>
          );
        }
        if (contentStr.includes('📝 Note') || contentStr.includes('Note:')) {
          return (
            <div className="pospeh-callout pospeh-callout-note">
              {children}
            </div>
          );
        }
        // Default blockquote
        return <blockquote {...props}>{children}</blockquote>;
      }
    }}
  >
    {content}
  </ReactMarkdown>
);

export const ChatWidget: React.FC<ChatWidgetProps> = ({ config }) => {
  // Initialize state from storage or defaults
  const storedData = useMemo(() => getStorageData(), []);

  // Create initial message as a proper assistant message
  const getInitialMessages = (): Message[] => {
    // If we have stored messages, use them
    if (storedData?.messages && storedData.messages.length > 0) {
      return storedData.messages;
    }
    // If initialMessage is configured and no stored messages, create initial assistant message
    if (config.initialMessage) {
      return [{
        id: 'initial-message',
        content: config.initialMessage,
        role: 'assistant',
        timestamp: new Date()
      }];
    }
    return [];
  };

  const [messages, setMessages] = useState<Message[]>(getInitialMessages());
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(storedData?.sessionId || null);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  // Floating chat states - restore from storage or use defaults
  const [isExpanded, setIsExpanded] = useState(() => {
    if (config.position !== 'bottom-right') return true;
    return storedData?.isExpanded ?? false;
  });
  const [position, setPosition] = useState(storedData?.position || { x: 20, y: 20 });
  const [size, setSize] = useState(storedData?.size || { width: 420, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);

  // Track if user has explicitly opened the chat (bypasses devMode on subsequent visits)
  const [hasUserOpened, setHasUserOpened] = useState(() => storedData?.hasUserOpened ?? false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const serviceRef = useRef(new ChatWidgetService(config.apiUrl));
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxCollapsedLength = config.maxCollapsedLength ?? 600;
  const expirationMinutes = config.sessionExpirationMinutes ?? DEFAULT_EXPIRATION_MINUTES;

  // Save to storage whenever relevant state changes
  // Save if: user has opened the chat, has an active session, or has messages
  useEffect(() => {
    if (hasUserOpened || sessionId || messages.length > 0) {
      saveStorageData({
        sessionId: sessionId || '',
        messages,
        position,
        size,
        isExpanded,
        lastMessageAt: new Date().toISOString(),
        expirationMinutes,
        hasUserOpened
      });
    }
  }, [sessionId, messages, position, size, isExpanded, expirationMinutes, hasUserOpened]);

  // Show chat if we have an active session or user has opened before
  useEffect(() => {
    if (storedData && (storedData.sessionId || storedData.hasUserOpened) && config.position === 'bottom-right') {
      setIsExpanded(storedData.isExpanded);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Drag and Resize handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = dragStart.x - e.clientX;
        const deltaY = dragStart.y - e.clientY;

        setPosition(prev => ({
          x: Math.max(0, prev.x + deltaX),
          y: Math.max(0, prev.y + deltaY)
        }));

        setDragStart({ x: e.clientX, y: e.clientY });
      }
      if (isResizing) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate new width (horizontal resize - keep right edge fixed)
        const newWidth = Math.max(350, viewportWidth - e.clientX - position.x);

        // Calculate new height (vertical resize - keep top edge fixed)
        const currentTop = viewportHeight - position.y - size.height;
        const newHeight = Math.max(400, e.clientY - currentTop);

        // Adjust bottom position to keep top edge fixed
        const newPositionY = Math.max(0, viewportHeight - e.clientY);

        setSize({
          width: newWidth,
          height: newHeight
        });

        setPosition(prev => ({
          x: prev.x,
          y: newPositionY
        }));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, position, size]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Lazy initialization - only called when first message is sent
  const initializeAndSendMessage = async (content: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Initialize chat session
      const response = await serviceRef.current.initializeChat({
        origin: window.location.origin
      });

      setSessionId(response.sessionId);

      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        content,
        role: 'user',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);

      // Send message
      const messageResponse = await serviceRef.current.sendMessage({
        sessionId: response.sessionId,
        message: content
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: messageResponse.response,
        role: 'assistant',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось инициализировать чат');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!sessionId) {
      // Lazy initialization with first message
      await initializeAndSendMessage(content);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);
    setInput('');

    try {
      const response = await serviceRef.current.sendMessage({
        sessionId,
        message: content
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response.response,
        role: 'assistant',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить сообщение');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    // Reset to initial message if configured, otherwise empty
    const initialMessages: Message[] = config.initialMessage
      ? [{
          id: 'initial-message',
          content: config.initialMessage,
          role: 'assistant',
          timestamp: new Date()
        }]
      : [];
    setMessages(initialMessages);
    setSessionId(null);
    setError(null);
    setExpandedMessages(new Set());
    setHasUserOpened(false);
    clearStorageData();
  };

  const handleDragStart = (e: React.MouseEvent) => {
    if (config.position === 'bottom-right' && isExpanded) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    // Mark as user-opened when expanding (this bypasses devMode on subsequent visits)
    if (newExpanded && !hasUserOpened) {
      setHasUserOpened(true);
    }
  };

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      handleSendMessage(input.trim());
      setInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleMessageExpansion = (id: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedMessages(newExpanded);
  };

  const renderMessage = (msg: Message) => {
    const isLong = msg.content.length > maxCollapsedLength;
    const isMessageExpanded = expandedMessages.has(msg.id);
    const displayContent = isLong && !isMessageExpanded
      ? msg.content.substring(0, maxCollapsedLength) + '...'
      : msg.content;

    return (
      <div
        key={msg.id}
        className={`pospeh-chat-message ${msg.role === 'user' ? 'pospeh-chat-message-user' : 'pospeh-chat-message-assistant'}`}
      >
        <div className={`pospeh-chat-bubble ${msg.role === 'user' ? 'pospeh-chat-bubble-user' : 'pospeh-chat-bubble-assistant'}`}>
          <div className="pospeh-chat-markdown">
            <MarkdownContent content={displayContent} isUser={msg.role === 'user'} />
          </div>
          {isLong && (
            <button
              onClick={() => toggleMessageExpansion(msg.id)}
              className="pospeh-chat-expand-button"
            >
              {isMessageExpanded ? (
                <>Скрыть <span className="pospeh-chevron">▲</span></>
              ) : (
                <>Показать полностью <span className="pospeh-chevron">▼</span></>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render chat icon button (collapsed state)
  if (config.position === 'bottom-right' && !isExpanded) {
    return (
      <div
        className={`pospeh-widget pospeh-chat-icon theme-${config.theme || 'default'}`}
        onClick={toggleExpanded}
      >
        {/* Chat Icon SVG */}
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>
    );
  }

  // Render expanded chat
  return (
    <div
      ref={containerRef}
      className={`pospeh-widget pospeh-chat-container theme-${config.theme || 'default'}`}
      style={{
        position: config.position === 'bottom-right' ? 'fixed' : 'relative',
        bottom: config.position === 'bottom-right' ? `${position.y}px` : undefined,
        right: config.position === 'bottom-right' ? `${position.x}px` : undefined,
        width: config.position === 'bottom-right' ? `${size.width}px` : '100%',
        maxWidth: config.position === 'bottom-right' ? 'none' : '800px',
        height: isMinimized ? 'auto' : (config.position === 'bottom-right' ? `${size.height}px` : '600px'),
        maxHeight: config.position === 'bottom-right' ? '90vh' : '800px'
      }}
    >
      {/* Header */}
      <div
        className="pospeh-chat-header"
        onMouseDown={handleDragStart}
      >
        <div className="pospeh-chat-header-left">
          <div className="pospeh-chat-status-dot"></div>
          <span className="pospeh-chat-title">AI Ассистент</span>
        </div>
        <div className="pospeh-chat-header-buttons">
          <button
            onClick={handleClearChat}
            className="pospeh-chat-header-button"
            title="Очистить чат"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
          </button>
          <button
            onClick={toggleMinimized}
            className="pospeh-chat-header-button"
            title={isMinimized ? 'Развернуть' : 'Свернуть'}
          >
            {isMinimized ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/>
                <polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </button>
          {config.position === 'bottom-right' && (
            <button
              onClick={toggleExpanded}
              className="pospeh-chat-header-button"
              title="Закрыть"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="pospeh-chat-messages">
            {messages.length === 0 && !config.initialMessage && (
              <div className="pospeh-chat-empty">
                <p className="pospeh-chat-empty-title">Задайте любой вопрос!</p>
                <p className="pospeh-chat-empty-subtitle">Я помогу вам разобраться и дам полезные рекомендации.</p>
              </div>
            )}
            {messages.map(renderMessage)}
            {isLoading && (
              <div className="pospeh-chat-loading">
                <div className="pospeh-spinner"></div>
                <span>AI думает...</span>
              </div>
            )}
            {error && (
              <div className="pospeh-chat-error">
                {error}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="pospeh-chat-input-container">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={config.placeholder || 'Введите сообщение...'}
              disabled={isLoading}
              className="pospeh-chat-input"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="pospeh-chat-send-button"
              title="Отправить"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

          {/* Resize handle */}
          {config.position === 'bottom-right' && (
            <div
              onMouseDown={handleResizeMouseDown}
              className="pospeh-chat-resize-handle"
            />
          )}
        </>
      )}
    </div>
  );
};
